import { Fragment, useLayoutEffect, useMemo, useState } from 'react'
import SearchableSelect, { type SelectOption } from '@/components/SearchableSelect'
import type { JournalEntryLine } from '@/services/accounting'
import { formatCurrencyField, parseDistributionAmount } from '@/utils/waterfallCalculator'

function formatMoney(value: number | string): string {
  const num = typeof value === 'string' ? Number(value) : value
  if (!Number.isFinite(num)) return String(value)
  return num.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function toNumber(value: number | string): number {
  const num = typeof value === 'string' ? Number(value) : value
  return Number.isFinite(num) ? num : 0
}

function newLineKey(): string {
  return crypto.randomUUID()
}

export type JournalEntryLineBadge = {
  label: string
  tone?: 'amber' | 'gray' | 'blue'
}

const SPLIT_LABEL_HEAD =
  'px-4 py-2.5 text-left font-medium text-gray-600 whitespace-nowrap'
const SPLIT_NUM_HEAD = 'px-4 py-2.5 text-right font-medium text-gray-600 whitespace-nowrap'
const SPLIT_LABEL_CELL = 'px-4 py-2.5 text-left whitespace-nowrap'
const SPLIT_NUM_CELL = 'px-4 py-2.5 text-right tabular-nums whitespace-nowrap'
const TOTALS_ROW_BG = 'bg-blue-50'
const TOTALS_ROW_BORDER = 'border-t-2 border-t-blue-200'

type Props = {
  lines: JournalEntryLine[]
  totalDebits?: number | string
  totalCredits?: number | string
  editableLocations?: boolean
  editableAmounts?: boolean
  editableLines?: boolean
  accountOptions?: SelectOption[]
  newLineDefaults?: Partial<JournalEntryLine>
  locationOptions?: SelectOption[]
  onLinesChange?: (lines: JournalEntryLine[]) => void
  lineBadges?: Record<number, JournalEntryLineBadge>
  /** Parallel to `lines`; when the label changes, a section header row is rendered. */
  lineSections?: string[]
  className?: string
  variant?: 'default' | 'split'
  hideHeader?: boolean
  scrollable?: boolean
  /** Default `'all'`. Use `'debits'` when credit lines are allowed to have no location. */
  locationRequiredOn?: 'all' | 'debits'
}

export default function JournalEntryPreview({
  lines,
  totalDebits,
  totalCredits,
  editableLocations = false,
  editableAmounts = false,
  editableLines = false,
  accountOptions = [],
  newLineDefaults,
  locationOptions = [],
  onLinesChange,
  lineBadges,
  lineSections,
  className = '',
  variant = 'default',
  hideHeader = false,
  scrollable = false,
  locationRequiredOn = 'all',
}: Props) {
  const [editingCell, setEditingCell] = useState<{
    index: number
    side: 'Debit' | 'Credit'
  } | null>(null)
  const [amountDraft, setAmountDraft] = useState('')
  const [lineKeys, setLineKeys] = useState<string[]>([])

  useLayoutEffect(() => {
    setLineKeys((prev) => {
      if (lines.length === prev.length) return prev
      if (lines.length > prev.length) {
        const extra = Array.from({ length: lines.length - prev.length }, () => newLineKey())
        return [...prev, ...extra]
      }
      return prev.slice(0, lines.length)
    })
  }, [lines.length])

  const effectiveAccountOptions = useMemo(() => {
    const byId = new Map(accountOptions.map((option) => [option.id, option]))
    for (const line of lines) {
      if (line.account_id && !byId.has(line.account_id)) {
        byId.set(line.account_id, {
          id: line.account_id,
          label: line.account_name || line.account_id,
        })
      }
    }
    return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label))
  }, [accountOptions, lines])

  const effectiveLocationOptions = useMemo(() => {
    const byId = new Map(locationOptions.map((option) => [option.id, option]))
    for (const line of lines) {
      const id = line.department_id?.trim()
      if (!id || byId.has(id)) continue
      byId.set(id, {
        id,
        label: line.department_name?.trim() || id,
      })
    }
    return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label))
  }, [locationOptions, lines])

  function lineDepartmentValue(line: JournalEntryLine): string | null {
    const id = line.department_id?.trim()
    return id ? id : null
  }

  const computedDebits = lines
    .filter((l) => l.posting_type === 'Debit')
    .reduce((sum, l) => sum + toNumber(l.amount), 0)
  const computedCredits = lines
    .filter((l) => l.posting_type === 'Credit')
    .reduce((sum, l) => sum + toNumber(l.amount), 0)

  const debits = editableAmounts ? computedDebits : totalDebits != null ? toNumber(totalDebits) : computedDebits
  const credits = editableAmounts ? computedCredits : totalCredits != null ? toNumber(totalCredits) : computedCredits
  const diff = Math.round((debits - credits) * 100) / 100
  const balanced = Math.abs(diff) < 0.005

  function updateLines(next: JournalEntryLine[]) {
    onLinesChange?.(next)
  }

  function updateLine(index: number, patch: Partial<JournalEntryLine>) {
    if (!onLinesChange) return
    updateLines(lines.map((line, i) => (i === index ? { ...line, ...patch } : line)))
  }

  function updateLineLocation(index: number, departmentId: string | null, label: string | null) {
    updateLine(index, {
      department_id: departmentId,
      department_name: label ?? '',
    })
  }

  function updateLineAccount(index: number, accountId: string | null, label: string | null) {
    if (!accountId || !label) return
    updateLine(index, {
      account_id: accountId,
      account_name: label,
    })
  }

  function commitAmountEdit(index: number, side: 'Debit' | 'Credit') {
    if (!onLinesChange) return
    setEditingCell(null)
    if (amountDraft.trim() === '') {
      updateLine(index, { posting_type: side, amount: 0 })
      return
    }
    const parsed = parseDistributionAmount(amountDraft)
    if (parsed == null) return
    updateLine(index, { posting_type: side, amount: parsed })
  }

  function renderAmountInput(index: number, line: JournalEntryLine, side: 'Debit' | 'Credit') {
    const isActiveSide = line.posting_type === side
    const displayed = isActiveSide ? toNumber(line.amount) : 0
    const isEditing = editingCell?.index === index && editingCell?.side === side

    if (!editableAmounts || !onLinesChange) {
      if (!isActiveSide) {
        return <span className="text-gray-300">—</span>
      }
      return formatMoney(line.amount)
    }

    return (
      <input
        type="text"
        inputMode="decimal"
        value={
          isEditing
            ? amountDraft
            : isActiveSide && displayed !== 0
              ? formatCurrencyField(displayed)
              : ''
        }
        placeholder="$0.00"
        onFocus={() => {
          setEditingCell({ index, side })
          setAmountDraft(
            isActiveSide && displayed !== 0 ? formatCurrencyField(displayed) : ''
          )
        }}
        onChange={(e) => setAmountDraft(e.target.value)}
        onBlur={() => commitAmountEdit(index, side)}
        className="w-full min-w-[6.5rem] rounded border border-gray-200 bg-white px-2 py-1 text-right text-sm tabular-nums"
      />
    )
  }

  function addLine(insertAfterIndex?: number) {
    if (!onLinesChange || effectiveAccountOptions.length === 0) return
    const firstAccount = effectiveAccountOptions[0]!
    const template = insertAfterIndex != null ? lines[insertAfterIndex] : lines[lines.length - 1]
    const nextLine: JournalEntryLine = {
      posting_type: 'Debit',
      amount: 0,
      account_id: firstAccount.id,
      account_name: firstAccount.label,
      description: '',
      department_id: newLineDefaults?.department_id ?? template?.department_id ?? null,
      department_name: newLineDefaults?.department_name ?? template?.department_name ?? null,
      entity_id: newLineDefaults?.entity_id ?? null,
      entity_name: newLineDefaults?.entity_name ?? null,
      entity_type: newLineDefaults?.entity_type ?? null,
    }
    const nextKey = newLineKey()
    if (insertAfterIndex != null) {
      const next = [...lines]
      next.splice(insertAfterIndex + 1, 0, nextLine)
      setLineKeys((prev) => {
        const nextKeys = [...prev]
        nextKeys.splice(insertAfterIndex + 1, 0, nextKey)
        return nextKeys
      })
      updateLines(next)
      return
    }
    setLineKeys((prev) => [...prev, nextKey])
    updateLines([...lines, nextLine])
  }

  function removeLine(index: number) {
    if (!onLinesChange || lines.length <= 1) return
    setLineKeys((prev) => prev.filter((_, i) => i !== index))
    updateLines(lines.filter((_, i) => i !== index))
  }

  const balanceBadge = (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
        balanced
          ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-100'
          : 'bg-red-50 text-red-800 ring-1 ring-red-100'
      }`}
    >
      {balanced ? 'Balanced' : diff > 0 ? `Unbalanced · Credits short ${formatMoney(Math.abs(diff))}` : `Unbalanced · Debits short ${formatMoney(Math.abs(diff))}`}
    </span>
  )

  if (variant === 'split') {
    return (
      <div
        className={`${scrollable ? 'flex min-h-0 flex-1 flex-col' : 'space-y-3'} ${className}`}
      >
        {!hideHeader ? (
          <h3 className="shrink-0 text-sm font-semibold text-gray-900">Journal entry preview</h3>
        ) : null}

        <div
          className={`overflow-auto rounded-lg border border-gray-200 ${
            scrollable ? 'h-0 min-h-0 w-full flex-1' : ''
          }`}
        >
          <table className="w-full min-w-[56rem] table-fixed border-separate border-spacing-0 text-sm">
            <thead className="sticky top-0 z-10 bg-gray-50">
              <tr>
                <th className={`${SPLIT_LABEL_HEAD} w-[22%]`}>Account</th>
                <th className={`${SPLIT_NUM_HEAD} w-[10%]`}>Debit</th>
                <th className={`${SPLIT_NUM_HEAD} w-[10%]`}>Credit</th>
                <th className={`${SPLIT_LABEL_HEAD} w-[20%]`}>Description</th>
                <th className={`${SPLIT_LABEL_HEAD} w-[12%]`}>Name</th>
                <th className={`${SPLIT_LABEL_HEAD} w-[14%]`}>Location</th>
                {editableLines ? <th className={`${SPLIT_NUM_HEAD} w-[5%]`} /> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {lines.map((line, index) => {
                const rowKey = lineKeys[index] ?? `pending-${index}`
                const badge = lineBadges?.[index]
                const missingLocation =
                  editableLocations &&
                  (locationRequiredOn === 'all' || line.posting_type === 'Debit') &&
                  !line.department_id &&
                  !line.department_name?.trim()
                const sectionLabel = lineSections?.[index]?.trim() || ''
                const prevSectionLabel = index > 0 ? lineSections?.[index - 1]?.trim() || '' : ''
                const nextSectionLabel =
                  index < lines.length - 1 ? lineSections?.[index + 1]?.trim() || '' : ''
                const showSectionHeader = Boolean(sectionLabel) && sectionLabel !== prevSectionLabel
                const showSectionSubtotal =
                  Boolean(sectionLabel) &&
                  (index === lines.length - 1 || sectionLabel !== nextSectionLabel)
                const colSpan = editableLines ? 7 : 6

                let sectionDebits = 0
                let sectionCredits = 0
                if (showSectionSubtotal) {
                  for (let i = 0; i < lines.length; i += 1) {
                    if ((lineSections?.[i]?.trim() || '') !== sectionLabel) continue
                    const sectionLine = lines[i]
                    if (!sectionLine) continue
                    const amount = toNumber(sectionLine.amount)
                    if (sectionLine.posting_type === 'Debit') sectionDebits += amount
                    else if (sectionLine.posting_type === 'Credit') sectionCredits += amount
                  }
                }
                const sectionDiff = Math.round((sectionDebits - sectionCredits) * 100) / 100
                const sectionBalanced = Math.abs(sectionDiff) < 0.005

                return (
                  <Fragment key={rowKey}>
                    {showSectionHeader ? (
                      <tr className="bg-gray-50">
                        <td
                          colSpan={colSpan}
                          className="border-t-2 border-gray-200 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600"
                        >
                          {sectionLabel}
                        </td>
                      </tr>
                    ) : null}
                  <tr className={missingLocation ? 'bg-red-50/60' : undefined}>
                    <td className={SPLIT_LABEL_CELL}>
                      {editableLines && onLinesChange && effectiveAccountOptions.length > 0 ? (
                        <SearchableSelect
                          options={effectiveAccountOptions}
                          value={line.account_id}
                          onChange={(id, label) => updateLineAccount(index, id, label)}
                          placeholder="Select account…"
                          className="w-full min-w-[10rem]"
                        />
                      ) : (
                        <>
                          <div className="font-medium text-gray-900">{line.account_name}</div>
                          {badge ? (
                            <span
                              className={`mt-0.5 inline-flex rounded px-1.5 py-0.5 text-[10px] font-medium ${
                                badge.tone === 'amber'
                                  ? 'bg-amber-50 text-amber-900 ring-1 ring-amber-100'
                                  : badge.tone === 'blue'
                                    ? 'bg-sky-50 text-sky-900 ring-1 ring-sky-100'
                                    : 'bg-gray-100 text-gray-700 ring-1 ring-gray-200'
                              }`}
                            >
                              {badge.label}
                            </span>
                          ) : null}
                        </>
                      )}
                    </td>
                    <td className={SPLIT_NUM_CELL}>
                      {renderAmountInput(index, line, 'Debit')}
                    </td>
                    <td className={SPLIT_NUM_CELL}>
                      {renderAmountInput(index, line, 'Credit')}
                    </td>
                    <td className={`${SPLIT_LABEL_CELL} truncate text-gray-700`} title={line.description}>
                      {editableLines && onLinesChange ? (
                        <input
                          type="text"
                          className="input w-full text-sm"
                          value={line.description ?? ''}
                          onChange={(e) => updateLine(index, { description: e.target.value })}
                        />
                      ) : (
                        line.description || '—'
                      )}
                    </td>
                    <td className={`${SPLIT_LABEL_CELL} truncate text-gray-700`} title={line.entity_name ?? ''}>
                      {line.entity_name || '—'}
                    </td>
                    <td className={`${SPLIT_LABEL_CELL} min-w-[9rem]`}>
                      {editableLocations && onLinesChange ? (
                        <SearchableSelect
                          options={effectiveLocationOptions}
                          value={lineDepartmentValue(line)}
                          onChange={(id, label) => updateLineLocation(index, id, label)}
                          placeholder="Select location…"
                          warning={missingLocation}
                          className="w-full min-w-[8.5rem]"
                        />
                      ) : (
                        <span className={missingLocation ? 'text-red-700' : 'text-gray-700'}>
                          {line.department_name || '—'}
                        </span>
                      )}
                    </td>
                    {editableLines ? (
                      <td className="px-2 py-1 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button
                            type="button"
                            className="btn btn-secondary h-7 w-7 p-0 text-base leading-none"
                            onClick={() => removeLine(index)}
                            disabled={lines.length <= 1}
                            aria-label="Remove line"
                          >
                            −
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary h-7 w-7 p-0 text-base leading-none"
                            onClick={() => addLine(index)}
                            disabled={effectiveAccountOptions.length === 0}
                            aria-label="Add line below"
                          >
                            +
                          </button>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                    {showSectionSubtotal ? (
                      <tr className="bg-gray-50/90">
                        <td
                          className={`${SPLIT_LABEL_CELL} border-t border-gray-200 align-top font-semibold text-gray-700`}
                        >
                          {sectionLabel} total
                        </td>
                        <td
                          className={`${SPLIT_NUM_CELL} border-t border-gray-200 align-top font-semibold text-gray-900`}
                        >
                          {formatMoney(sectionDebits)}
                        </td>
                        <td
                          className={`${SPLIT_NUM_CELL} border-t border-gray-200 align-top font-semibold text-gray-900`}
                        >
                          <div>{formatMoney(sectionCredits)}</div>
                          <div
                            className={`mt-1 text-[11px] font-medium ${
                              sectionBalanced ? 'text-emerald-700' : 'text-red-700'
                            }`}
                          >
                            {sectionBalanced
                              ? 'Balanced'
                              : sectionDiff > 0
                                ? `Credits short ${formatMoney(Math.abs(sectionDiff))}`
                                : `Debits short ${formatMoney(Math.abs(sectionDiff))}`}
                          </div>
                        </td>
                        <td
                          colSpan={editableLines ? 4 : 3}
                          className="border-t border-gray-200 align-top"
                        />
                      </tr>
                    ) : null}
                  </Fragment>
                )
              })}
            </tbody>
            <tfoot className="sticky bottom-0 z-10">
              <tr className={`${TOTALS_ROW_BG} ${TOTALS_ROW_BORDER} [&>td]:align-top`}>
                <td className={`${SPLIT_LABEL_CELL} font-bold text-gray-900`}>Totals</td>
                <td className={`${SPLIT_NUM_CELL} font-bold text-gray-900`}>{formatMoney(debits)}</td>
                <td className={`${SPLIT_NUM_CELL} font-bold text-gray-900`}>
                  <div>{formatMoney(credits)}</div>
                  <div className="mt-2 flex justify-end">{balanceBadge}</div>
                </td>
                <td colSpan={editableLines ? 4 : 3} />
              </tr>
            </tfoot>
          </table>
        </div>

      </div>
    )
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {!hideHeader ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-900">Journal entry preview</h3>
          {balanceBadge}
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="data-table min-w-full text-sm">
          <thead>
            <tr>
              <th>Account</th>
              <th className="text-right">Debit</th>
              <th className="text-right">Credit</th>
              <th>Description</th>
              <th>Name</th>
              <th>Location</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => {
              const rowKey = lineKeys[index] ?? `pending-${index}`
              const badge = lineBadges?.[index]
              const missingLocation =
                editableLocations &&
                (locationRequiredOn === 'all' || line.posting_type === 'Debit') &&
                !line.department_id &&
                !line.department_name?.trim()
              return (
                <tr
                  key={rowKey}
                  className={missingLocation ? 'bg-red-50/60' : undefined}
                >
                  <td className="min-w-[10rem]">
                    <div className="font-medium text-gray-900">{line.account_name}</div>
                    {badge ? (
                      <span
                        className={`mt-0.5 inline-flex rounded px-1.5 py-0.5 text-[10px] font-medium ${
                          badge.tone === 'amber'
                            ? 'bg-amber-50 text-amber-900 ring-1 ring-amber-100'
                            : badge.tone === 'blue'
                              ? 'bg-sky-50 text-sky-900 ring-1 ring-sky-100'
                              : 'bg-gray-100 text-gray-700 ring-1 ring-gray-200'
                        }`}
                      >
                        {badge.label}
                      </span>
                    ) : null}
                  </td>
                  <td className="text-right tabular-nums">
                    {line.posting_type === 'Debit' ? formatMoney(line.amount) : ''}
                  </td>
                  <td className="text-right tabular-nums">
                    {line.posting_type === 'Credit' ? formatMoney(line.amount) : ''}
                  </td>
                  <td className="max-w-[14rem] truncate text-gray-700" title={line.description}>
                    {line.description || '—'}
                  </td>
                  <td className="max-w-[10rem] truncate text-gray-700" title={line.entity_name ?? ''}>
                    {line.entity_name || '—'}
                  </td>
                  <td className="min-w-[9rem]">
                    {editableLocations && onLinesChange ? (
                      <SearchableSelect
                        options={effectiveLocationOptions}
                        value={lineDepartmentValue(line)}
                        onChange={(id, label) => updateLineLocation(index, id, label)}
                        placeholder="Select location…"
                        warning={missingLocation}
                        className="w-full min-w-[8.5rem]"
                      />
                    ) : (
                      <span className={missingLocation ? 'text-red-700' : 'text-gray-700'}>
                        {line.department_name || '—'}
                      </span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
          <tfoot>
            <tr className="border-t border-gray-200 bg-gray-50/80 font-medium">
              <td>Totals</td>
              <td className="text-right tabular-nums">{formatMoney(debits)}</td>
              <td className="text-right tabular-nums">
                <div>{formatMoney(credits)}</div>
                <div className="mt-2 flex justify-end">{balanceBadge}</div>
              </td>
              <td colSpan={3} />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}
